import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink, Globe, MapPin, Calendar, Shield } from "lucide-react";
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import FadeInSection from "@/components/FadeInSection";
import InlineCTA from "@/components/InlineCTA";
import LazySection from "@/components/LazySection";
import NewsletterBanner from "@/components/NewsletterBanner";
import { platformPages } from "@/data/platformPages";
import { useSanityPlatformPages } from "@/hooks/useSanityContent";

const PlatformPage = () => {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const slug = paramSlug || window.location.pathname.replace(/^\/|\/$/g, "");
  const { data: sanityPages } = useSanityPlatformPages();

  // Use Sanity as source of truth; only fall back to hardcoded if Sanity hasn't loaded
  const sanityPage = sanityPages?.find((p) => p.slug?.current === slug);
  const hardcodedPage = sanityPages === undefined ? platformPages.find((p) => p.slug === slug) : null;

  const page = sanityPage
    ? {
        headline: sanityPage.headline,
        description: sanityPage.description ?? "",
        tags: sanityPage.tags ?? [],
        founded: sanityPage.founded,
        headquarters: sanityPage.headquarters,
        regulation: sanityPage.regulation,
        website: sanityPage.website,
        sections: sanityPage.sections ?? [],
        slug,
      }
    : hardcodedPage;

  if (!page) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Platform not found</h1>
          <Link to="/prediction-market-apps" className="text-primary hover:underline">Back to Apps & Platforms</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO title={page.headline} description={page.description} canonical={`/${page.slug}`} breadcrumbs={[{ name: "Home", href: "/" }, { name: "Apps & Platforms", href: "/prediction-market-apps" }, { name: page.headline, href: `/${page.slug}` }]} />
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <div className="mb-8">
          <InlineCTA variant="newsletter" />
        </div>

        <FadeInSection>
          <Link to="/prediction-market-apps" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Apps & Platforms
          </Link>
          <h1 className="text-3xl md:text-4xl font-extrabold text-foreground mb-3">{page.headline}</h1>
          <p className="text-muted-foreground text-lg max-w-3xl mb-6">{page.description}</p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {page.tags.map((tag) => (
              <span key={tag} className="text-xs font-semibold tracking-wider uppercase px-2.5 py-1 rounded border border-border text-muted-foreground">{tag}</span>
            ))}
          </div>

          {/* Quick facts */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
            {page.founded && (
              <div className="rounded-lg border border-border bg-card p-3">
                <Calendar className="w-4 h-4 text-primary mb-1" />
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Founded</p>
                <p className="text-sm font-bold text-foreground">{page.founded}</p>
              </div>
            )}
            {page.headquarters && (
              <div className="rounded-lg border border-border bg-card p-3">
                <MapPin className="w-4 h-4 text-primary mb-1" />
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">HQ</p>
                <p className="text-sm font-bold text-foreground">{page.headquarters}</p>
              </div>
            )}
            {page.regulation && (
              <div className="rounded-lg border border-border bg-card p-3">
                <Shield className="w-4 h-4 text-primary mb-1" />
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Regulation</p>
                <p className="text-sm font-bold text-foreground">{page.regulation}</p>
              </div>
            )}
            {page.website && (
              <div className="rounded-lg border border-border bg-card p-3">
                <Globe className="w-4 h-4 text-primary mb-1" />
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Website</p>
                <a href={page.website} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-primary hover:underline inline-flex items-center gap-1">
                  Visit <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        </FadeInSection>

        {page.sections.map((section, i) => (
          <FadeInSection key={i} delay={0.05 + i * 0.03}>
            <div className="mb-8">
              <h2 className="text-xl font-bold text-foreground mb-3">{section.heading}</h2>
              <p className="text-base text-muted-foreground leading-relaxed">{section.content}</p>
            </div>
          </FadeInSection>
        ))}

        <div className="mt-10">
          <LazySection><NewsletterBanner /></LazySection>
        </div>
      </div>
    </div>
  );
};

export default PlatformPage;
